import { Suspense, useEffect } from 'react';
import { useLocation, Outlet, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuthStore } from '@/lib/stores/auth';
import { buildCommandItems } from '@/lib/commandItems';
import { CommandPalette } from '@/components/CommandPalette';
import { LocaleSync } from '@/components/LocaleSync';
import RouterProgress from './ui/RouterProgress';
import Layout from './Layout';
import HomeSkeleton from './skeletons/HomeSkeleton';
import MarketplaceSkeleton from './skeletons/MarketplaceSkeleton';
import AgentDetailSkeleton from './skeletons/AgentDetailSkeleton';
import CartSkeleton from './skeletons/CartSkeleton';
import AuthSkeleton from './skeletons/AuthSkeleton';
import AdminTableSkeleton from './skeletons/AdminTableSkeleton';

export default function LocaleLayout() {
  const { t, i18n } = useTranslation();
  const location = useLocation();
  const navigate = useNavigate();
  const { isAuthenticated } = useAuthStore();

  const locale = location.pathname.split('/')[1];

  // 身份验证检查：如果未登录且访问受保护页面，重定向到登录页
  useEffect(() => {
    const path = location.pathname;
    // 受保护的路由前缀
    const protectedPaths = ['/dashboard', '/cart'];
    const isProtected = protectedPaths.some(p => path.includes(p));
    // 公开页面（不需要重定向）
    const publicPaths = ['/login', '/register', '/forgot-password', '/reset-password'];
    const isPublic = publicPaths.some(p => path.includes(p));

    if (isProtected && !isAuthenticated && !isPublic) {
      // 重定向到登录页，附带 redirect 参数
      navigate(`/${locale}/login?redirect=${encodeURIComponent(path)}`);
    }
  }, [location.pathname, isAuthenticated, locale, navigate]);

  // 同步 URL locale 到 i18n（避免在渲染期间调用 changeLanguage）
  useEffect(() => {
    if (['en', 'zh-CN'].includes(locale) && i18n.language !== locale) {
      i18n.changeLanguage(locale);
    }
  }, [locale, i18n]);

  const commandItems = buildCommandItems({ locale, isAuthenticated, t, navigate });

  return (
    <Layout>
      <LocaleSync />
      <RouterProgress />
      <Suspense fallback={<PageSkeleton />}>
        <Outlet />
      </Suspense>
      <CommandPalette items={commandItems} />
    </Layout>
  );
}

function PageSkeleton() {
  const location = useLocation();
  const path = location.pathname;

  if (path.includes('/dashboard/admin')) return <AdminTableSkeleton />;
  if (path.includes('/marketplace/')) return <AgentDetailSkeleton />;
  if (path.includes('/marketplace')) return <MarketplaceSkeleton />;
  if (path.includes('/cart')) return <CartSkeleton />;
  if (path.includes('/login') || path.includes('/register') || path.includes('/auth/callback')) return <AuthSkeleton />;
  return <HomeSkeleton />;
}
